import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Share2, Pin, Check,
  Moon, Sun, X, ArrowUpRight,
  Layers, ExternalLink, ShieldCheck
} from 'lucide-react';
import { api, type PortalWidgetData } from '../lib/api';
import { R, ft, shadow, label } from '../lib/ui';
import { useTheme, toggle } from '../lib/theme';
import { docIcon } from '../lib/doc-icons';
import { Spinner } from '../components/Spinner';

export function PublicPortal() {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Получаем данные портала
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-public'],
    queryFn: () => api.portalGet(),
  });

  // Проверяем, авторизован ли текущий пользователь в системе
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me().catch(() => null),
    retry: false,
  });

  const filteredWidgets = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.widgets;
    const q = search.toLowerCase();
    return data.widgets.filter((w) => {
      const matchTitle = w.title.toLowerCase().includes(q);
      const matchDesc = w.description?.toLowerCase().includes(q) ?? false;
      const matchSheet = w.stats?.sheetName?.toLowerCase().includes(q) ?? false;
      return matchTitle || matchDesc || matchSheet;
    });
  }, [data, search]);

  const handleCopy = (e: React.MouseEvent, token: string) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: R.bg,
        color: R.text,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Archivo, system-ui, -apple-system, sans-serif',
      }}
    >
      {/* ---------- ВЕРХНЯЯ НАВИГАЦИОННАЯ ПАНЕЛЬ ---------- */}
      <header
        style={{
          height: 60,
          borderBottom: ft.edge,
          background: R.bgSubtle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/favicon.png"
            alt="Redwood Tables"
            width={34}
            height={34}
            style={{
              width: 34,
              height: 34,
              borderRadius: 6,
              objectFit: 'contain',
              display: 'block',
            }}
          />
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Redwood Tables
            </div>
            <div style={{ ...label, fontSize: 9.5, color: R.accentText }}>
              {data?.config.badge || 'Государственные реестры'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {me && me.role !== 'none' && (
            <a
              href="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 32,
                padding: '0 12px',
                borderRadius: 4,
                border: ft.edge,
                background: R.accentSubtle,
                color: R.accentText,
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              <ShieldCheck size={14} />
              <span>Рабочая область</span>
            </a>
          )}

          <button
            onClick={() => toggle()}
            title="Переключить тему оформления"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 12px',
              borderRadius: 4,
              border: ft.edge,
              background: R.bgInput,
              color: R.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            <span>{theme === 'dark' ? 'Светлая' : 'Тёмная'}</span>
          </button>
        </div>
      </header>

      {/* ---------- HERO СЕКЦИЯ ---------- */}
      <section
        style={{
          padding: '40px 24px 32px',
          maxWidth: 1200,
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 840, margin: '0 auto 36px' }}>
          {/* БАННЕР ПОРТАЛА (675x338) БЕЗ БОКСА И БЕЗ ГРАНИЦ */}
          <div
            style={{
              marginBottom: 26,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src="/redwood_banner.png"
              alt="Государственные реестры Redwood"
              width={675}
              height={338}
              style={{
                maxWidth: '100%',
                width: 675,
                height: 'auto',
                aspectRatio: '675 / 338',
                objectFit: 'contain',
                display: 'block',
                border: 'none',
                boxShadow: 'none',
                outline: 'none',
              }}
            />
          </div>

          <h1
            style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.15,
              marginBottom: 12,
              color: R.text,
            }}
          >
            {data?.config.title || 'Государственные реестры Redwood'}
          </h1>

          <p
            style={{
              fontSize: 'clamp(14px, 1.8vw, 16px)',
              color: R.textMuted,
              lineHeight: 1.55,
              margin: '0 auto',
            }}
          >
            {data?.config.subtitle ||
              'Официальный публичный портал открытых данных, лицензий и государственных реестров.'}
          </p>
        </div>

        {/* ПОИСК */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Search
              size={18}
              color={R.textMuted}
              style={{ position: 'absolute', left: 16, pointerEvents: 'none' }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по государственным реестрам и названиям..."
              style={{
                width: '100%',
                height: 46,
                padding: '0 40px 0 46px',
                fontSize: 14,
                borderRadius: 8,
                border: ft.strong,
                background: R.bgInput,
                color: R.text,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute',
                  right: 12,
                  padding: 4,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: R.textMuted,
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* ---------- СПИСОК НАВИГАЦИОННЫХ ПЛАШЕК (LINK CARDS) ---------- */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Spinner size={32} />
          </div>
        ) : error ? (
          <div
            style={{
              padding: 30,
              background: R.dangerSubtle,
              border: `1px solid ${R.dangerBorder}`,
              borderRadius: 8,
              color: R.danger,
              textAlign: 'center',
            }}
          >
            Не удалось загрузить данные реестров. Пожалуйста, попробуйте позже.
          </div>
        ) : filteredWidgets.length === 0 ? (
          <div
            style={{
              padding: 60,
              textAlign: 'center',
              color: R.textMuted,
              background: R.bgSubtle,
              border: ft.edge,
              borderRadius: 8,
            }}
          >
            {search ? 'По вашему запросу реестров не найдено.' : 'Пока нет опубликованных реестров.'}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: 20,
            }}
          >
            {filteredWidgets.map((widget) => {
              const targetUrl = `/shared/${widget.shareToken}`;

              return (
                <NavigationalLinkCard
                  key={widget.id}
                  widget={widget}
                  targetUrl={targetUrl}
                  isCopied={copiedToken === widget.shareToken}
                  onCopy={(e) => handleCopy(e, widget.shareToken)}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* ---------- ФУТЕР ---------- */}
      <footer
        style={{
          marginTop: 'auto',
          borderTop: ft.edge,
          padding: '24px',
          textAlign: 'center',
          fontSize: 12.5,
          color: R.textMuted,
          background: R.bgSubtle,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <strong>Redwood Tables</strong> — Единая система государственных реестров
          </div>
          <div>
            Все данные синхронизируются в реальном времени
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---------------- НАВИГАЦИОННАЯ ПЛАШКА / LINK CARD ----------------

interface NavigationalLinkCardProps {
  widget: PortalWidgetData;
  targetUrl: string;
  isCopied: boolean;
  onCopy: (e: React.MouseEvent) => void;
}

function NavigationalLinkCard({
  widget,
  targetUrl,
  isCopied,
  onCopy,
}: NavigationalLinkCardProps) {
  const [hover, setHover] = useState(false);
  const Icon = docIcon(widget.icon || 'sheet');

  return (
    <a
      href={targetUrl}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        background: R.bgSubtle,
        border: `1px solid ${hover ? R.accent : R.border}`,
        borderRadius: 10,
        padding: '20px 22px',
        gap: 14,
        boxShadow: hover
          ? '0 12px 30px -4px rgba(131, 12, 16, 0.18)'
          : shadow.panel,
        transform: hover ? 'translateY(-3px)' : 'none',
        transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      {/* АКЦЕНТНАЯ ПОЛОСА СВЕРХУ ПРИ НАВЕДЕНИИ */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: R.accent,
          opacity: hover ? 1 : 0,
          transition: 'opacity 0.18s ease',
        }}
      />

      {/* ВЕРХНЯЯ СТРОКА: ЗАКРЕПЛЕНО И ИКОНКА */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          {widget.isPinned ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 8px',
                borderRadius: 12,
                background: 'rgba(217, 160, 102, 0.15)',
                color: R.warning,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              <Pin size={10} />
              Закреплено
            </span>
          ) : (
            <div style={{ height: 18 }} />
          )}
        </div>

        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: hover ? R.accent : R.bgElevated,
            color: hover ? R.onAccent : R.accent,
            border: ft.hair,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
          }}
        >
          <Icon size={18} />
        </div>
      </div>

      {/* ЗАГОЛОВОК И ОПИСАНИЕ */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 6,
          }}
        >
          <h3
            style={{
              fontSize: 17,
              fontWeight: 800,
              letterSpacing: '-0.015em',
              lineHeight: 1.25,
              color: hover ? R.accent : R.text,
              margin: 0,
              transition: 'color 0.15s ease',
            }}
          >
            {widget.title}
          </h3>

          <div
            style={{
              color: hover ? R.accent : R.textMuted,
              transform: hover ? 'translate(2px, -2px)' : 'none',
              transition: 'all 0.15s ease',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <ArrowUpRight size={18} />
          </div>
        </div>

        {widget.description && (
          <p
            style={{
              fontSize: 13,
              color: R.textMuted,
              lineHeight: 1.48,
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {widget.description}
          </p>
        )}
      </div>



      {/* НИЖНЯЯ ПАНЕЛЬ ДЕЙСТВИЙ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingTop: 8,
          borderTop: ft.hair,
          marginTop: 2,
        }}
      >
        <div
          style={{
            flex: 1,
            height: 34,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            background: hover ? 'var(--rt-acc-hover)' : R.accent,
            color: R.onAccent,
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 700,
            transition: 'background 0.12s ease',
          }}
        >
          <span>Открыть в новой вкладке</span>
          <ExternalLink size={13} />
        </div>

        <button
          onClick={onCopy}
          title="Скопировать ссылку на таблицу"
          style={{
            height: 34,
            padding: '0 10px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            background: R.bgInput,
            border: ft.edge,
            borderRadius: 6,
            color: isCopied ? R.success : R.textMuted,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {isCopied ? <Check size={14} /> : <Share2 size={14} />}
          <span>{isCopied ? 'Скопировано' : 'Ссылка'}</span>
        </button>
      </div>
    </a>
  );
}
